import { getCookie, setCookie } from '@/utils/cookies';

const ENDPOINT =
    process.env.NODE_ENV === 'development'
        ? '/device/v1/devices'
        : 'https://api.edge-gateway.siriusxm.com/device/v1/devices';

const COOKIE_NAME = 'DEVICE_GRANT';
const COOKIE_DOMAIN = '.siriusxm.com';
const TENANT = 'sxm';
const APP_NAME = 'web-marketing';
const APP_VERSION = '1.0';
const GRANT_VERSION = 'v2';
const MOBILE_BREAKPOINT = '(max-width: 767px)';

export interface DeviceGrant {
    deviceId: string;
    grant: string;
    grantExpiresAt: string;
    grantVersion: string;
    refreshGrant: string;
    refreshGrantExpiresAt: string;
}

interface BrowserInfo {
    name: string;
    version: string;
}

let inFlight: Promise<DeviceGrant | null> | null = null;

export async function ensureDeviceGrant(): Promise<DeviceGrant | null> {
    if (typeof window === 'undefined') {
        return null;
    }

    const cached = readGrantCookie();
    if (cached) {
        return cached;
    }

    if (!inFlight) {
        inFlight = fetchGrant().finally(() => {
            inFlight = null;
        });
    }

    return inFlight;
}

function readGrantCookie(): DeviceGrant | null {
    const raw = getCookie(COOKIE_NAME);
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as DeviceGrant;
        if (!parsed.deviceId || !parsed.grantExpiresAt) {
            return null;
        }

        if (new Date(parsed.grantExpiresAt).getTime() <= Date.now()) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
}

async function fetchGrant(): Promise<DeviceGrant | null> {
    try {
        const response = await fetch(ENDPOINT, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'x-sxm-tenant': TENANT,
            },
            body: JSON.stringify(buildRequestBody()),
        });

        if (!response.ok) {
            return null;
        }

        const grant = (await response.json()) as DeviceGrant;
        if (!grant?.deviceId || !grant.grantExpiresAt) {
            return null;
        }

        writeGrantCookie(grant);
        return grant;
    } catch {
        return null;
    }
}

function writeGrantCookie(grant: DeviceGrant): void {
    setCookie(COOKIE_NAME, JSON.stringify(grant), {
        domain: COOKIE_DOMAIN,
        path: '/',
        expires: new Date(grant.grantExpiresAt),
        secure: true,
        sameSite: 'Lax',
    });
}

function buildRequestBody() {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const { name, version } = parseBrowser(ua);
    const isMobile =
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(MOBILE_BREAKPOINT).matches
            : false;

    return {
        devicePlatform: isMobile ? 'web-mobile' : 'web-desktop',
        deviceAttributes: {
            browser: {
                app: APP_NAME,
                appVersion: APP_VERSION,
                browser: name,
                browserVersion: version,
                userAgent: ua,
            },
        },
        grantVersion: GRANT_VERSION,
    };
}

function parseBrowser(ua: string): BrowserInfo {
    if (!ua) {
        return { name: 'Unknown', version: '0' };
    }

    const patterns: Array<{ name: string; regex: RegExp }> = [
        { name: 'Edge', regex: /Edg(?:e|A|iOS)?\/([\d.]+)/ },
        { name: 'Chrome', regex: /Chrome\/([\d.]+)/ },
        { name: 'Firefox', regex: /Firefox\/([\d.]+)/ },
        { name: 'Safari', regex: /Version\/([\d.]+).*Safari/ },
    ];

    for (const { name, regex } of patterns) {
        const match = ua.match(regex);
        if (match) {
            return { name, version: match[1] };
        }
    }

    return { name: 'Unknown', version: '0' };
}
